import React, { useState, useEffect, useCallback } from 'react';
import { RequireStaff } from '@/components/RequireStaff';
import { useLanguage } from '@/contexts/LanguageContext';
import { db } from '@/lib/firebase';
import {
  collection, query, orderBy, onSnapshot, doc, updateDoc,
  getDoc, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ShieldAlert, CheckCircle2, XCircle, Loader2, ExternalLink, Search } from 'lucide-react';

const T = {
  en: {
    title: 'Identity Verification Reviews',
    subtitle: 'Manual review required for professional licenses (Consultants & Partners)',
    search: 'Search by name, email...',
    empty: 'No pending reviews.',
    noMatch: 'No reviews match your search.',
    role: 'Role', session: 'Session', received: 'Received',
    licenses: 'Licenses', specializations: 'Specializations',
    diditSession: 'Open Didit session',
    viewProfile: 'View profile', approve: 'Approve', decline: 'Decline',
    approving: 'Approving...', declining: 'Declining...',
    approvedToast: 'User approved and activated.',
    declinedToast: 'User declined.',
    errorToast: 'Action failed — see console.',
  },
  fr: {
    title: 'Révisions de vérification d\'identité',
    subtitle: 'Révision manuelle requise pour les licences professionnelles (Consultants & Partenaires)',
    search: 'Rechercher par nom, email...',
    empty: 'Aucune révision en attente.',
    noMatch: 'Aucune révision ne correspond à la recherche.',
    role: 'Rôle', session: 'Session', received: 'Reçu',
    licenses: 'Licences', specializations: 'Spécialisations',
    diditSession: 'Ouvrir la session Didit',
    viewProfile: 'Voir le profil', approve: 'Approuver', decline: 'Refuser',
    approving: 'Approbation...', declining: 'Refus...',
    approvedToast: 'Utilisateur approuvé et activé.',
    declinedToast: 'Utilisateur refusé.',
    errorToast: "Échec de l'action — voir la console.",
  },
};

interface StaffNotification {
  id: string;
  type: string;
  userId: string;
  sessionId: string;
  role: string;
  message: string;
  read: boolean;
  createdAt?: any;
}

interface UserPreview {
  displayName?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  diditVerification?: any;
  registrationData?: {
    licenses?: Array<{ number?: string; details?: string; jurisdictions?: string[]; otherJurisdictions?: string }>;
    specializations?: Array<{ expertises?: string[]; industry?: string; experienceYears?: string; spokenLanguages?: string[] }>;
  };
}

export default function StaffVerifications() {
  const { language } = useLanguage();
  const t = T[language] || T.en;

  const [notifications, setNotifications] = useState<StaffNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [userCache, setUserCache] = useState<Record<string, UserPreview>>({});
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'staffNotifications'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() } as StaffNotification))
          .filter((n) => n.type === 'verification_review' && !n.read);
        setNotifications(items);
        setLoading(false);
      },
      (err) => {
        console.error('[StaffVerifications] listener error:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Charge les infos utilisateur (nom, email, licences, spécialisations, Didit) pour chaque notif visible
  useEffect(() => {
    const missing = notifications
      .map((n) => n.userId)
      .filter((uid) => uid && !userCache[uid]);
    if (missing.length === 0) return;

    (async () => {
      const updates: Record<string, UserPreview> = {};
      for (const uid of missing) {
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          if (snap.exists()) updates[uid] = snap.data() as UserPreview;
        } catch (err) {
          console.warn('[StaffVerifications] could not load user', uid, err);
        }
      }
      setUserCache((prev) => ({ ...prev, ...updates }));
    })();
  }, [notifications, userCache]);

  const handleDecision = useCallback(
    async (notif: StaffNotification, decision: 'approved' | 'declined') => {
      setActingOn(notif.id);
      try {
        const newStatus = decision === 'approved' ? 'active' : 'rejected';

        await updateDoc(doc(db, 'users', notif.userId), {
          status: newStatus,
          'diditVerification.manualReviewStatus': decision,
          'diditVerification.manualReviewAt': serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        await updateDoc(doc(db, 'staffNotifications', notif.id), {
          read: true,
          resolvedAs: decision,
          resolvedAt: serverTimestamp(),
        });

        // Email de suivi après révision manuelle, tous rôles — voir
        // diditWebhook.ts pour le cas Lender (email automatique dès Didit
        // "Approved"). Ici on couvre le cas de la révision manuelle staff.
        const userSnap = await getDoc(doc(db, 'users', notif.userId));
        const userData = userSnap.exists() ? userSnap.data() : null;
        if (userData?.email) {
          await addDoc(collection(db, 'emailQueue'), {
            type: 'manual_review_decision',
            to: userData.email,
            firstName: userData.firstName || userData.displayName?.split(' ')[0] || '',
            lastName: userData.lastName || '',
            role: notif.role,
            language: userData.language || userData.preferredLanguage || 'en',
            decision, // 'approved' | 'declined'
            dashboardUrl: decision === 'approved'
              ? `https://acquizgroup.com/login`
              : undefined,
            status: 'pending',
            createdAt: serverTimestamp(),
          });
        }
      } catch (err) {
        console.error('[StaffVerifications] decision failed:', err);
        alert(t.errorToast);
      } finally {
        setActingOn(null);
      }
    },
    [t.errorToast]
  );

  const filtered = notifications.filter((notif) => {
    if (!search.trim()) return true;
    const user = userCache[notif.userId];
    const q = search.toLowerCase();
    const name = user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    return (
      (name || '').toLowerCase().includes(q) ||
      (user?.email || '').toLowerCase().includes(q) ||
      notif.role.toLowerCase().includes(q)
    );
  });

  return (
    <RequireStaff>
      <div className="space-y-4 font-['Geist',sans-serif]">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg relative">
              <ShieldAlert className="w-6 h-6 text-amber-400" />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {notifications.length}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{t.title}</h1>
              <p className="text-gray-400 text-sm">{t.subtitle}</p>
            </div>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder={t.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
          </div>
        </header>

        <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.03]">
          {loading ? (
            <div className="flex justify-center p-12">
              <Loader2 className="w-5 h-5 animate-spin text-white/30" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">{t.empty}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">{t.noMatch}</div>
          ) : (
            <ScrollArea className="max-h-[calc(100vh-260px)]">
              <div className="divide-y divide-white/5">
                {filtered.map((notif) => {
                  const user = userCache[notif.userId];
                  const isActing = actingOn === notif.id;
                  const verif = user?.diditVerification;
                  const licenses = user?.registrationData?.licenses || [];
                  const specializations = user?.registrationData?.specializations || [];

                  return (
                    <div key={notif.id} className="p-4 flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-white font-medium text-sm">
                            {user?.displayName ||
                              `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
                              notif.userId}
                          </p>
                          {user?.email && (
                            <p className="text-xs text-gray-500">{user.email}</p>
                          )}
                          <p className="text-sm text-gray-300 mt-1">{notif.message}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300 shrink-0">
                          {notif.role}
                        </Badge>
                      </div>

                      {verif && (
                        <div className="text-xs text-gray-400 bg-black/20 rounded-lg p-2 space-y-0.5">
                          {verif.entityType && <p>Type: {verif.entityType}</p>}
                          {verif.documentType && <p>Document: {verif.documentType}</p>}
                          {verif.nationality && <p>Nationalité: {verif.nationality}</p>}
                          {verif.companyName && <p>Entreprise: {verif.companyName}</p>}
                        </div>
                      )}

                      {licenses.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase text-gray-500 tracking-wide">{t.licenses}</p>
                          <div className="space-y-0.5">
                            {licenses.map((l, i) => (
                              <p key={i} className="text-xs text-gray-300">
                                #{l.number}{l.details ? ` — ${l.details}` : ''}
                                {Array.isArray(l.jurisdictions) && l.jurisdictions.length > 0 && (
                                  <span className="text-gray-500"> ({l.jurisdictions.join(', ')})</span>
                                )}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {specializations.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase text-gray-500 tracking-wide">{t.specializations}</p>
                          <div className="space-y-0.5">
                            {specializations.map((s, i) => (
                              <p key={i} className="text-xs text-gray-300">
                                {Array.isArray(s.expertises) ? s.expertises.join(', ') : ''}
                                {s.industry ? ` — ${s.industry}` : ''}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 flex-wrap">
                        <a
                          href={`/profile/public/${notif.userId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                        >
                          {t.viewProfile} <ExternalLink className="w-3 h-3" />
                        </a>
                        {verif?.sessionUrl && (
                          <a
                            href={verif.sessionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-amber-400 hover:underline flex items-center gap-1"
                          >
                            {t.diditSession} <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        <span className="text-[10px] text-gray-600">
                          {t.session}: {notif.sessionId.slice(0, 12)}...
                        </span>

                        <div className="ml-auto flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isActing}
                            onClick={() => handleDecision(notif, 'declined')}
                            className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-7 text-xs"
                          >
                            {isActing ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <XCircle className="w-3 h-3 mr-1" />
                            )}
                            {t.decline}
                          </Button>
                          <Button
                            size="sm"
                            disabled={isActing}
                            onClick={() => handleDecision(notif, 'approved')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                          >
                            {isActing ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                            )}
                            {t.approve}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </RequireStaff>
  );
}